import { useState } from 'react';
import { LazyProcessMapDialog, useProcessMapDialogData } from '@/components/process-flow';
import { usePreviousValue } from '@/hooks/renderPhaseState';
import {
  ReviewProcessStepSection,
  type ReviewProcessStepSectionProps
} from './ReviewProcessStepSection';

/**
 * The step preview plus the map it opens — the shared answer to "which step of
 * which process is this task?" for every host that does not already hold the
 * process.
 *
 * The map is offered only once `useProcessMapDialogData` reports it can be: the
 * process read has to have SUCCEEDED (an unreadable process is a 404 by design
 * for "not yours", and a button that opens an empty diagram is worse than no
 * button) and the viewer has to pass `useCanReadOrgCatalogs`. While that read
 * runs the button shows busy; when it fails the button goes away, which costs
 * nothing visually because it is overlaid on the preview frame rather than laid
 * out in it.
 */
export interface StepPreviewWithProcessMapProps extends Omit<
  ReviewProcessStepSectionProps,
  'onShowInMap' | 'showInMapPending'
> {
  /**
   * The HOST surface's open state — the task or survey modal this preview sits
   * in, not the map's. The process is fetched while the user reads the preview,
   * so the button is already decided by the time they look for it.
   */
  hostOpen: boolean;
  /** Absent ⇒ no map (and no queries at all). */
  processId?: string;
  /**
   * Which node is the viewer's own step. Per-surface and passed explicitly —
   * never derivable from `sourceProcessStep`, which is the PREDECESSOR.
   */
  highlightNodeId?: string | null;
}

export function StepPreviewWithProcessMap({
  hostOpen,
  processId,
  highlightNodeId,
  ...section
}: StepPreviewWithProcessMapProps) {
  const [mapOpen, setMapOpen] = useState(false);
  const openMap = () => setMapOpen(true);
  const { isReady, isPending, content } = useProcessMapDialogData(processId, { hostOpen, mapOpen });

  // A host that closes under an open map (a submit, a step change) must not
  // leave the map behind, and must not reopen onto it later.
  const wasHostOpen = usePreviousValue(hostOpen);
  if (wasHostOpen && !hostOpen) setMapOpen(false);

  return (
    <>
      <ReviewProcessStepSection
        {...section}
        onShowInMap={isReady || isPending ? openMap : undefined}
        showInMapPending={isPending}
      />
      {/* Mounted unconditionally: a boundary that first appears with `open`
          already true never observes the false→true flip, and focus restore
          on the first open silently breaks. */}
      <LazyProcessMapDialog
        {...content}
        open={mapOpen}
        onOpenChange={setMapOpen}
        highlightNodeId={highlightNodeId}
      />
    </>
  );
}
